import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Group as GroupIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  AccountCircle
} from '@mui/icons-material';
import teamService, { Team } from '../services/teamService';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';

const TeamDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requestingAccess, setRequestingAccess] = useState(false);

  const loadTeamDetails = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await teamService.getTeamById(parseInt(id));
      setTeam(data);
    } catch (err: any) {
      setError('Failed to load team details. Please try again later.');
      console.error('Error loading team details:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadTeamDetails();
  }, [loadTeamDetails]);

  const handleRequestAccess = async (studentId: number) => {
    if (!team) return;

    try {
      setRequestingAccess(true);
      await teamService.requestTeamAccess({
        teamId: team.id,
        studentId: studentId,
        action: 'join'
      });
      // Show success message
      alert('Access request sent successfully!');
    } catch (err) {
      console.error('Error requesting team access:', err);
      setError('Failed to request team access. Please try again.');
    } finally {
      setRequestingAccess(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'ACTIVE':
        return 'success';
      case 'INACTIVE':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!team) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        <AppBar position="static">
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Student Psychology Profile System
            </Typography>
            <Typography variant="body1" sx={{ mr: 2 }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <IconButton color="inherit" onClick={handleProfileClick}>
              <AccountCircle />
            </IconButton>
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
          <Box textAlign="center" py={5}>
            <Typography variant="h6" color="text.secondary">
              Team not found
            </Typography>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/teams')}
              sx={{ mt: 2 }}
            >
              Back to Teams
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Student Psychology Profile System
          </Typography>
          <Typography variant="body1" sx={{ mr: 2 }}>
            {user?.firstName} {user?.lastName}
          </Typography>
          <IconButton color="inherit" onClick={handleProfileClick}>
            <AccountCircle />
          </IconButton>
          <Button color="inherit" onClick={handleLogout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
        <Box mb={3}>
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton onClick={() => navigate('/teams')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
            {team.name}
          </Typography>
          <Chip
            label={team.status}
            color={getStatusColor(team.status)}
            size="medium"
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Team Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  {team.departmentName && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" mb={2}>
                        <GroupIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Department
                          </Typography>
                          <Typography variant="body1">
                            {team.departmentName}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {team.location && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" mb={2}>
                        <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Location
                          </Typography>
                          <Typography variant="body1">
                            {team.location}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {team.email && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" mb={2}>
                        <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Email
                          </Typography>
                          <Typography variant="body1">
                            {team.email}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {team.phone && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" mb={2}>
                        <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Phone
                          </Typography>
                          <Typography variant="body1">
                            {team.phone}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Created At
                        </Typography>
                        <Typography variant="body1">
                          {format(new Date(team.createdAt), 'MMM dd, yyyy')}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Actions
              </Typography>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                startIcon={<PersonAddIcon />}
                onClick={() => navigate(`/teams/${team.id}/request-access`)}
                disabled={team.status !== 'ACTIVE' || requestingAccess}
              >
                Request Access for Student
              </Button>
            </Paper>

            {team.membershipStatus && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Membership Status
                </Typography>
                <Chip
                  label={team.membershipStatus}
                  color={
                    team.membershipStatus === 'approved' ? 'success' :
                    team.membershipStatus === 'pending' ? 'warning' :
                    'error'
                  }
                  sx={{ mt: 1 }}
                />
              </Paper>
            )}
          </Grid>
        </Grid>
      </Box>
      </Container>
    </Box>
  );
};

export default TeamDetailsPage;